import I18n from 'i18n-js';
import React, { useContext } from 'react';

import { RCCheckbox, RCSwitch } from '@reverbdotcom/cadence/components';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { US } from '@reverbdotcom/commons/src/constants';
import { MoneyInput } from '@reverbdotcom/commons/src/components/money_input';
import { BRAND_NEW } from '@reverbdotcom/commons/src/condition_helpers';
import { FormCards } from '@reverbdotcom/commons/src/components/FormCards';

import PriceBreakdown from '../PriceBreakdown';
import { Field, ActionType } from '../../sellFormReducer';
import { SellFormContext, IShopConfig } from '../../SellFormContext';
import RecommendedPriceRange from '../../../../recommended_price_range/recommended_price_range';
import SellFormPricingGuidance from '../pricing_guidance/SellFormPricingGuidance';
import SellFormTransactionsModalContainer from '../SellFormPricingModal/SellFormTransactionsModalContainer';
import SellFormLiveListingsModalContainer from '../SellFormPricingModal/SellFormLiveListingsModalContainer';
import { core_apimessages_Listing_SeedType as SeedType } from '../../../../../schemas/rql';
import { SellFormScheduledPriceDrop } from '../SellFormScheduledPriceDrop';
import lightningBolt from '../../../../../../images/icons/lightning-bolt.svg';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { isExperimentEnabled } from '@reverbdotcom/commons/src/user_context_helpers';
import experiments from '@reverbdotcom/commons/src/experiments';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';

const COMPONENT_NAME = 'PricingSection';

export function isShopInUSA(shopConfig: IShopConfig) {
  return shopConfig.address.countryCode === US;
}

function TransactionsAndLiveListingsContainer({ canonicalProductId }) {
  return (
    <div className="mt-2 mobile-mt-1 d-flex fx-dir-row fx-justify-start fx-align-center tablet-fx-dir-col tablet-fx-align-start">
      <SellFormTransactionsModalContainer canonicalProductIds={[canonicalProductId]} />
      <SellFormLiveListingsModalContainer canonicalProductIds={[canonicalProductId]} />
    </div>
  );
}

export function PricingSection() {
  const user = useUser();
  const isV2PricingGuidanceEnabled = isExperimentEnabled(user, experiments.SELL_FORM_PRICING_GUIDANCE);

  const {
    formState,
    dispatch,
    loading,
    errorMap,
    shopConfig,
    listingCreateFlow,
    showSellingFeeWaived,
    sellFormViewData,
  } = useContext(SellFormContext);
  const {
    currencySetting: currency,
    hasAccountRep,
    offerPolicy,
    preferredSeller,
    taxPolicies = [],
  } = shopConfig;
  const { autoRejectLowOffers, lowballPercentage } = offerPolicy ?? {};
  const showVatTaxExemptField = taxPolicies.length > 0 && taxPolicies.some(({ vatPolicy }) => !!vatPolicy);

  const canonicalProductId = sellFormViewData?.fetchedListing?.canonicalProductId || (
    formState[Field.SEED_TYPE] === SeedType.CanonicalProduct && formState[Field.SEED_ID]
  );

  const defaultLocaleContent = formState[Field.LOCALIZED_CONTENTS].find((lc) => lc.defaultForShop);
  const title = [defaultLocaleContent[Field.MAKE], defaultLocaleContent[Field.MODEL], defaultLocaleContent[Field.FINISH]].filter(Boolean).join(' ');
  const isBrandNew = formState[Field.CONDITION_SLUG] === BRAND_NEW;
  const adminOrAccountRep = hasAccountRep || user.isAdmin;
  const showMapField = isExperimentEnabled(user, experiments.SHOW_SELLER_SUGGESTED_MAP) && isBrandNew && adminOrAccountRep;

  const showLegacyPricingGuidance = isShopInUSA(shopConfig) && !!canonicalProductId && !isV2PricingGuidanceEnabled;

  useViewTracking({
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ComponentView,
    listingCreateFlow,
    experiments: formatExperimentsForEventAttributes(user, [
      experiments.SELL_FORM_PRICING_GUIDANCE,
    ]),
  }, true);

  return (
    <>
      <FormCards.Card>
        {showLegacyPricingGuidance && (
          <RecommendedPriceRange
            canonicalProduct={{ title, id: canonicalProductId }}
            conditionSlug={formState[Field.CONDITION_SLUG]}
            transactionsPanel={<TransactionsAndLiveListingsContainer canonicalProductId={canonicalProductId} />}
          />
        )}

        {isV2PricingGuidanceEnabled && (
          <SellFormPricingGuidance
            canonicalProduct={{ title, id: canonicalProductId }}
            conditionSlug={formState[Field.CONDITION_SLUG]}
            shopConfig={shopConfig}
          />
        )}

        <FormCards.FormSection>
          <div className="width-rem-50 tablet-max-width-70 mobile-max-width-100">
            <MoneyInput
              id={Field.PRICE}
              name={Field.PRICE}
              label={I18n.t(`discovery.sellForm.fields.${Field.PRICE}.label`)}
              value={formState[Field.PRICE]?.amount || ''}
              onChange={(event) => dispatch({
                type: ActionType.UPDATE_FIELD,
                fieldKey: Field.PRICE,
                value: { amount: event.target.value },
              })}
              tag="required"
              required
              disabled={loading}
              errorText={errorMap[Field.PRICE]}
              currencyCode={currency}
            />

            <PriceBreakdown
              price={formState[Field.PRICE]?.amount}
              currency={currency}
              preferredSeller={preferredSeller}
              sellingFeeWaived={showSellingFeeWaived}
            />

            {showVatTaxExemptField && (
              <>
                <div className="weight-bold size-90 mt-3 mb-1">
                  {I18n.t(`discovery.sellForm.fields.${Field.TAX_EXEMPT}.checkboxTitle`)}
                </div>
                <RCCheckbox
                  name={Field.TAX_EXEMPT}
                  id={Field.TAX_EXEMPT}
                  label={I18n.t(`discovery.sellForm.fields.${Field.TAX_EXEMPT}.checkboxLabel`)}
                  checked={formState[Field.TAX_EXEMPT]}
                  onChange={() => dispatch({
                    type: ActionType.UPDATE_FIELD,
                    fieldKey: Field.TAX_EXEMPT,
                    value: !formState[Field.TAX_EXEMPT],
                  })}
                  disabled={loading}
                />
              </>
            )}

            {showMapField && (
              <div className="mt-2">
                <MoneyInput
                  id={Field.SELLER_REPORTED_MAP}
                  name={Field.SELLER_REPORTED_MAP}
                  label={I18n.t('discovery.sellForm.fields.sellerReportedMap.label')}
                  value={formState[Field.SELLER_REPORTED_MAP]?.amount || ''}
                  onChange={(event) => dispatch({
                    type: ActionType.UPDATE_FIELD,
                    fieldKey: Field.SELLER_REPORTED_MAP,
                    value: { amount: event.target.value },
                  })}
                  disabled={loading}
                  errorText={errorMap[Field.SELLER_REPORTED_MAP]}
                  currencyCode={currency}
                  helpText={I18n.t('discovery.sellForm.fields.sellerReportedMap.helpText')}
                />
              </div>
            )}
          </div>
        </FormCards.FormSection>
        <FormCards.ToggledSection
          id="seller-cost-collapse"
          title={I18n.t(`discovery.sellForm.fields.${Field.SELLER_COST}.descriptionHeading`)}
          defaultOpen={!!formState[Field.SELLER_COST]?.amount}
        >
          <div className="pr-10 mobile-pr-0">
            <p>{I18n.t(`discovery.sellForm.fields.${Field.SELLER_COST}.descriptionContent`)}</p>
          </div>
          <MoneyInput
            id={Field.SELLER_COST}
            name={Field.SELLER_COST}
            label={I18n.t(`discovery.sellForm.fields.${Field.SELLER_COST}.label`)}
            value={formState[Field.SELLER_COST]?.amount || ''}
            onChange={(event) => dispatch({
              type: ActionType.UPDATE_FIELD,
              fieldKey: Field.SELLER_COST,
              value: { amount: event.target.value },
            })}
            disabled={loading}
            errorText={errorMap[Field.SELLER_COST]}
            currencyCode={currency}
            helpText={I18n.t(`discovery.sellForm.fields.${Field.SELLER_COST}.helpText`)}
          />
        </FormCards.ToggledSection>
      </FormCards.Card>

      <FormCards.Card>
        <FormCards.FormSection>
          <div className="width-rem-50 tablet-max-width-70 mobile-max-width-100">
            <div className="mb-2 d-flex fx-align-center">
              <img
                src={lightningBolt}
                alt={I18n.t('discovery.sellForm.fields.sellYourGearFaster.heading')}
                className="mr-1"
              />
              <span className="size-110 weight-bold">
                {I18n.t('discovery.sellForm.fields.sellYourGearFaster.heading')}
              </span>
            </div>
            <p>
              {isV2PricingGuidanceEnabled ?
                I18n.t('discovery.sellForm.fields.sellYourGearFaster.increaseChancesOfSelling') :
                I18n.t('discovery.sellForm.fields.sellYourGearFaster.description')}
            </p>

            <SellFormScheduledPriceDrop/>

            <div className="mt-2">
              <RCSwitch
                name={Field.OFFERS_ENABLED}
                id={Field.OFFERS_ENABLED}
                label={I18n.t(`discovery.sellForm.fields.${Field.OFFERS_ENABLED}.checkboxLabelForScheduledPriceDropExperiment`)}
                sublabel={
                  autoRejectLowOffers ?
                    I18n.t(`discovery.sellForm.fields.${Field.OFFERS_ENABLED}.checkboxSublabel`) + I18n.t(`discovery.sellForm.fields.${Field.OFFERS_ENABLED}.lowballText`, { lowOfferThreshold: lowballPercentage })
                    : I18n.t(`discovery.sellForm.fields.${Field.OFFERS_ENABLED}.checkboxSublabel`)
                }
                checked={formState[Field.OFFERS_ENABLED]}
                onChange={() => dispatch({
                  type: ActionType.UPDATE_FIELD,
                  fieldKey: Field.OFFERS_ENABLED,
                  value: !formState[Field.OFFERS_ENABLED],
                })}
                disabled={loading}
              />
            </div>
          </div>
        </FormCards.FormSection>
      </FormCards.Card>
    </>
  );
}
